import { useState, type CSSProperties, type ReactElement, type SelectHTMLAttributes } from "react";
import { palette, radius, typography } from "@ux/tokens";

// Solid (non-translucent) colors for the native <option> popup. The
// closed-state <select> uses palette.surfaceInset (semi-transparent) for
// the inset treatment over the dark page, but browsers render <option>
// against the OS-default white surface, so we MUST give them an opaque
// dark background or text becomes unreadable on Windows (rgba composites
// to a tan/cream tone over white).
const optionStyle: CSSProperties = {
    backgroundColor: "#15171c",
    color: "#f0f0f2",
};

export interface SelectOption<TValue extends string = string> {
    readonly value: TValue;
    readonly label: string;
    readonly disabled?: boolean;
}

export interface SelectProps<TValue extends string = string>
    extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "style" | "children"> {
    readonly options: readonly SelectOption<TValue>[];
    readonly placeholder?: string;
    readonly invalid?: boolean;
}

export function Select<TValue extends string = string>({
    options,
    placeholder,
    invalid = false,
    disabled,
    ...rest
}: SelectProps<TValue>): ReactElement {
    const [focused, setFocused] = useState(false);

    const borderColor = invalid
        ? palette.danger
        : focused
          ? palette.borderFocus
          : palette.border;

    const style: CSSProperties = {
        width: "100%",
        padding: "8px 12px",
        borderRadius: radius.md,
        border: `1px solid ${borderColor}`,
        background: palette.surfaceInset,
        color: palette.fg,
        fontFamily: typography.family.body,
        fontSize: typography.size.md,
        outline: "none",
        appearance: "none",
        transition: "border-color 120ms ease",
        opacity: disabled === true ? 0.55 : 1,
        // The browser-rendered native dropdown popup uses this hint to
        // theme its option list. Without it, options render with OS-default
        // white background and our near-white fg color is unreadable.
        colorScheme: "dark",
    };

    return (
        <select
            {...rest}
            disabled={disabled}
            style={style}
            onFocus={(e) => {
                setFocused(true);
                rest.onFocus?.(e);
            }}
            onBlur={(e) => {
                setFocused(false);
                rest.onBlur?.(e);
            }}
        >
            {placeholder !== undefined ? (
                <option value="" disabled style={optionStyle}>
                    {placeholder}
                </option>
            ) : null}
            {options.map((option) => (
                <option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled === true}
                    style={optionStyle}
                >
                    {option.label}
                </option>
            ))}
        </select>
    );
}
